import { useMemo, useState } from 'react';
import useApi from './useApi';

const buildDatasetMap = (datasets = []) => {
  const map = new Map();
  datasets.forEach((dataset) => {
    if (dataset?.id) {
      map.set(dataset.id, dataset.label || dataset.id);
    }
  });
  return map;
};

const withinDays = (dateValue, days) => {
  if (!dateValue) return false;
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return false;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return parsed.getTime() >= cutoff;
};

const resolveWidgetsCount = (definition) => {
  if (!definition) return null;
  if (Array.isArray(definition?.dashboard?.widgets)) return definition.dashboard.widgets.length;
  if (Array.isArray(definition?.extensions?.widgets)) return definition.extensions.widgets.length;
  return null;
};

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (_err) {
      return null;
    }
  }
  return value;
};

const normalizeReportRow = (report = {}) => {
  const metadata = report.metadata || report.meta || null;
  const definition =
    parseMaybeJson(report.definition) ||
    parseMaybeJson(report?.definitionJson) ||
    parseMaybeJson(metadata?.definition) ||
    {};
  const scope = report.scope ?? metadata?.scope ?? definition.scope ?? null;
  const baseDataset =
    report.baseDataset ??
    report.base_dataset ??
    metadata?.baseDataset ??
    metadata?.base_dataset ??
    definition.baseDataset ??
    definition.base_dataset ??
    null;
  const widgetsCount = Number.isFinite(report.widgetsCount)
    ? report.widgetsCount
    : Number.isFinite(report.widgets_count)
      ? report.widgets_count
      : resolveWidgetsCount(definition);
  return {
    ...report,
    scope,
    baseDataset,
    widgetsCount,
    createdAt: report.createdAt ?? report.created_at ?? metadata?.createdAt ?? metadata?.created_at ?? null,
    updatedAt: report.updatedAt ?? report.updated_at ?? metadata?.updatedAt ?? metadata?.updated_at ?? null,
    lastRunAt: report.lastRunAt ?? report.last_run_at ?? metadata?.lastRunAt ?? metadata?.last_run_at ?? null,
    queryTimeMs: report.queryTimeMs ?? report.query_time_ms ?? metadata?.queryTimeMs ?? metadata?.query_time_ms ?? null,
  };
};

const useReportsCatalog = () => {
  const {
    useReports,
    useReportDatasets,
    useArchiveReport,
    useUnarchiveReport,
    useUpdateReport,
    useDbLogs,
  } = useApi();

  const reportsQuery = useReports();
  const datasetsQuery = useReportDatasets();
  const logsQuery = useDbLogs({ level: 'warning,error', limit: 100 });

  const archiveReport = useArchiveReport();
  const unarchiveReport = useUnarchiveReport();
  const updateReport = useUpdateReport();

  const [filters, setFilters] = useState({
    scope: 'all',
    dataset: 'all',
    search: '',
    startDate: '',
    endDate: '',
    recentOnly: false,
  });
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  const rawReports = useMemo(
    () => (Array.isArray(reportsQuery.data?.rows) ? reportsQuery.data.rows : []),
    [reportsQuery.data]
  );
  const reports = useMemo(() => rawReports.map(normalizeReportRow), [rawReports]);
  const datasetOptions = useMemo(() => datasetsQuery.data?.datasets || [], [datasetsQuery.data]);
  const datasetMap = useMemo(() => buildDatasetMap(datasetOptions), [datasetOptions]);

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (filters.scope !== 'all' && String(report.scope || '').toLowerCase() !== filters.scope) {
        return false;
      }
      if (filters.dataset !== 'all' && report.baseDataset !== filters.dataset) {
        return false;
      }
      if (filters.search) {
        const tagText = Array.isArray(report.tags) ? report.tags.join(' ') : String(report.tags || '');
        const haystack = `${report.name || ''} ${report.description || ''} ${tagText}`.toLowerCase();
        if (!haystack.includes(filters.search.toLowerCase())) return false;
      }
      if (filters.recentOnly && !withinDays(report.createdAt, 7)) {
        return false;
      }
      if (filters.startDate) {
        const start = new Date(filters.startDate);
        const created = new Date(report.createdAt);
        if (created.getTime() < start.getTime()) return false;
      }
      if (filters.endDate) {
        const end = new Date(filters.endDate);
        const created = new Date(report.createdAt);
        if (created.getTime() > end.getTime()) return false;
      }
      return true;
    });
  }, [filters, reports]);

  const activeReports = filteredReports.filter((report) => report.status !== 'archived');
  const archivedReports = filteredReports.filter((report) => report.status === 'archived');

  const summary = useMemo(() => {
    const activeCount = reports.filter((report) => report.status !== 'archived').length;
    const recentCount = reports.filter((report) => withinDays(report.createdAt, 7)).length;
    const scopeCounts = reports.reduce((acc, report) => {
      const scope = report.scope || 'unknown';
      acc[scope] = (acc[scope] || 0) + 1;
      return acc;
    }, {});
    const topScope = Object.entries(scopeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
    return {
      total: reports.length,
      active: activeCount,
      recent: recentCount,
      topScope,
    };
  }, [reports]);

  const reportLogs = useMemo(() => {
    const rows = Array.isArray(logsQuery.data) ? logsQuery.data : [];
    return rows
      .filter((log) => String(log?.event || '').startsWith('reports.'))
      .slice(0, 6);
  }, [logsQuery.data]);

  return {
    reports,
    activeReports,
    archivedReports,
    datasetOptions,
    datasetMap,
    filters,
    setFilters,
    summary,
    reportLogs,
    archivedOpen,
    setArchivedOpen,
    editingReport,
    setEditingReport,
    reportsQuery,
    archiveReport,
    unarchiveReport,
    updateReport,
  };
};

export default useReportsCatalog;
